import { NavigationContainer } from '@react-navigation/native';
import Images from 'assets/Images';
import Lang from 'assets/Lang';
import BaseStatusBar from 'common/components/bases/BaseStatusBar';
import { documentTitleFormatter } from 'common/components/views/DocumentTitle';
import Funcs from 'common/helpers/Funcs';
import ExceptionService from 'common/services/ExceptionService';
import FirebaseService from 'common/services/FirebaseService';
import StorageService from 'common/services/StorageService';
import StorageConst from 'consts/StorageConst';
import UIConst from 'consts/UIConst';
import React, { useEffect, useState } from 'react';
import RNCodePush from '@code-push-next/react-native-code-push';
import { Image, Platform, Pressable, StyleSheet, Text, View } from 'react-native';
import { SafeAreaProvider } from 'react-native-safe-area-context';
import SupportView from 'screens/containers/SupportView';
import ThemeAction from 'states/context/actions/ThemeAction';
import AppProvider from 'states/context/providers/AppProvider';
import ThemeProvider from 'states/context/providers/ThemeProvider';
import Configs from 'utils/Configs';
import Utils from 'utils/Utils';
import messaging from '@react-native-firebase/messaging';
import NavigationService from './src/common/services/NavigationService';

messaging().setBackgroundMessageHandler(async (remoteMessage) => {
  console.log('FCM message handled in background:', remoteMessage);
});

class AppErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, errorInfo) {
    FirebaseService.logError(error, errorInfo);
  }

  onPressRestart = () => {
    RNCodePush.restartApp();
  };

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;
    const message = Lang.appError;

    return (
      <View style={styles.errorContainer}>
        <View style={styles.errorCard}>
          <View style={styles.errorIcon}>
            <Image source={Images.logo} style={styles.logo} resizeMode="contain" />
          </View>
          <Text style={styles.errorEyebrow}>{message.eyebrow}</Text>
          <Text style={styles.errorTitle}>{message.title}</Text>
          <Text style={styles.errorDetail}>{message.detail}</Text>
          <Pressable style={styles.errorButton} onPress={this.onPressRestart}>
            <Text style={styles.errorButtonText}>{message.reload}</Text>
          </Pressable>
          <SupportView style={styles.supportView} />
        </View>
      </View>
    );
  }
}

const App = () => {
  const [RootView, setRootView] = useState(null);
  const [IntroView, setIntroView] = useState(null);
  const [showIntro, setShowIntro] = useState(true);
  const onFinishIntro = () => {
    setShowIntro(false);
    StorageService.save(StorageConst.SHOW_INTRO, true);
  };
  useEffect(() => {
    async function init() {
      try {
        // Bắt lỗi
        ExceptionService.init((message) => {
          ExceptionService.pushException(message);
        });

        // Storage
        let darkMode = await StorageService.get(StorageConst.DARK_MODE);
        darkMode = darkMode !== undefined && darkMode !== null ? darkMode : false;
        ThemeAction.changeTheme(darkMode);

        // Font chữ ưu tiên lấy offline cho từng máy
        const fontSizeType = await StorageService.get(StorageConst.FONT_SIZE_TYPE);
        if (fontSizeType >= 0 && fontSizeType !== null) {
          Configs.settings.fontSizeType = fontSizeType;
          Funcs.log('Local fontsize', fontSizeType);
        }
        UIConst.changeFontSize();

        // Lazy delay loading rootview để những biến trên có tác dụng
        if (__DEV__ && Configs.debugScreen) {
          const module = await import('./src/screens/TestScreen');
          setRootView(() => module.default);
        } else {
          const module = await import('RootView');
          setRootView(() => module.default);
        }

        // Intro
        Lang.change();
        let hasIntro = await StorageService.get(StorageConst.SHOW_INTRO);
        // hasIntro = false;
        let showIntro1 = !hasIntro;
        showIntro1 = showIntro1 && !Configs.token;
        showIntro1 = showIntro1 && Utils.products?.length < 1;
        showIntro1 = showIntro1 && Platform.OS !== 'web';
        setShowIntro(showIntro1);
        if (showIntro1) {
          const moduleIntro = await import('IntroView');
          setIntroView(() => moduleIntro.default);
        }
      } catch (err) {
        console.log('###', err);
      }

      // Unmount
      return () => {};
    }
    init();
  }, []);

  return (
    <NavigationContainer ref={(ref) => NavigationService.setNavigator(ref)} documentTitle={{ formatter: documentTitleFormatter }}>
      <SafeAreaProvider>
        <ThemeProvider>
          <AppProvider>
            <AppErrorBoundary>
              <BaseStatusBar />
              {RootView ? <RootView /> : null}
              {showIntro && IntroView && <IntroView onFinish={onFinishIntro} />}
            </AppErrorBoundary>
          </AppProvider>
        </ThemeProvider>
      </SafeAreaProvider>
    </NavigationContainer>
  );
};

const styles = StyleSheet.create({
  errorContainer: { flex: 1, justifyContent: 'center', padding: 24, backgroundColor: '#F4F7FB' },
  errorCard: {
    alignItems: 'center',
    paddingHorizontal: 24,
    paddingVertical: 32,
    borderRadius: 24,
    backgroundColor: '#FFFFFF',
    shadowColor: '#183B56',
    shadowOffset: { width: 0, height: 12 },
    shadowOpacity: 0.12,
    shadowRadius: 24,
    elevation: 8
  },
  logoContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    width: 76,
    height: 76,
    marginBottom: 24,
    borderRadius: 20,
    backgroundColor: '#F8FAFC'
  },
  logo: { width: 46, height: 46 },
  errorIcon: {
    alignItems: 'center',
    justifyContent: 'center',
    width: 64,
    height: 64,
    marginBottom: 20,
    borderRadius: 32,
    backgroundColor: '#FEE2E2'
  },
  errorIconText: { color: '#DC2626', fontSize: 36, fontWeight: '700', lineHeight: 43 },
  errorEyebrow: { marginBottom: 8, color: '#DC2626', fontSize: 14, fontWeight: '700' },
  errorTitle: { marginBottom: 12, color: '#102A43', fontSize: 22, fontWeight: '700', textAlign: 'center' },
  errorDetail: { color: '#627D98', fontSize: 14, textAlign: 'center' },
  errorButton: { width: '100%', marginTop: 28, paddingVertical: 14, borderRadius: 12, backgroundColor: '#1677FF' },
  supportView: { marginTop: 16, marginBottom: 0 },
  errorButtonText: { color: '#FFFFFF', fontSize: 16, fontWeight: '700', textAlign: 'center' }
});

export default RNCodePush({ checkFrequency: RNCodePush.CheckFrequency.MANUAL })(App);
